import {
  catalogProducts,
  type CatalogProduct,
} from "./accessoryQuoteCatalog";

export const accessoryCategoryBrowserBrands = [
  "All brands",
  "Bobrick",
  "ASI",
  "Bradley",
] as const;

export type AccessoryCategoryBrowserBrand = (typeof accessoryCategoryBrowserBrands)[number];

export type AccessoryCategoryBrowserGroupId =
  | "accessibility-safety"
  | "handwashing-hygiene"
  | "paper-waste"
  | "compartment-accessories"
  | "mirrors-storage"
  | "family-specialty";

/**
 * Maps only the product families represented in the owner-supplied reference-price catalog.
 * Specialty/family products are intentionally quote-led until a priced owner source is supplied.
 */
export const accessoryCatalogCategoriesByGroup: Record<AccessoryCategoryBrowserGroupId, readonly string[]> = {
  "accessibility-safety": ["Grab Bars", "Shower & Bath"],
  "handwashing-hygiene": ["Hygiene & Soap"],
  "paper-waste": ["Paper & Waste"],
  "compartment-accessories": ["Compartment Accessories"],
  "mirrors-storage": ["Mirrors", "Hooks & Storage"],
  "family-specialty": [],
};

const allBrandModelReferences: Partial<Record<AccessoryCategoryBrowserGroupId, Record<Exclude<AccessoryCategoryBrowserBrand, "All brands">, string>>> = {
  "accessibility-safety": {
    Bobrick: "B-5806.99x12",
    ASI: "ASI 3701-12",
    Bradley: "8120-001120",
  },
  "handwashing-hygiene": {
    Bobrick: "B-2111",
    ASI: "ASI 0347",
    Bradley: "6542",
  },
  "paper-waste": {
    Bobrick: "B-36903",
    ASI: "ASI 0469",
    Bradley: "234-00",
  },
  "compartment-accessories": {
    Bobrick: "B-221",
    ASI: "ASI 0477-SM",
    Bradley: "583-00",
  },
  "mirrors-storage": {
    Bobrick: "B-165 1824",
    ASI: "ASI 0535-1830",
    Bradley: "740-016240",
  },
};

export function getAccessoryCategoryCatalogProducts(
  groupId: AccessoryCategoryBrowserGroupId,
  brand: AccessoryCategoryBrowserBrand = "All brands",
): CatalogProduct[] {
  const categories = accessoryCatalogCategoriesByGroup[groupId];

  return catalogProducts
    .filter((product) => categories.includes(product.category))
    .filter((product) => brand === "All brands" || product.brand === brand)
    .sort((left, right) => left.model.localeCompare(right.model, undefined, { numeric: true }));
}

/**
 * Keeps the category-page browser compact. "All brands" shows one current model from each
 * reference brand side-by-side; an individual brand shows up to three current models.
 */
export function getAccessoryCategoryBrowserFeaturedProducts(
  groupId: AccessoryCategoryBrowserGroupId,
  brand: AccessoryCategoryBrowserBrand,
): CatalogProduct[] {
  if (brand !== "All brands") {
    return getAccessoryCategoryCatalogProducts(groupId, brand).slice(0, 3);
  }

  const references = allBrandModelReferences[groupId];
  if (!references) return [];

  return (["Bobrick", "ASI", "Bradley"] as const).flatMap((referenceBrand) => {
    const model = references[referenceBrand];
    const product = getAccessoryCategoryCatalogProducts(groupId, referenceBrand)
      .find((candidate) => candidate.model === model);
    return product ? [product] : [];
  });
}

export function getAccessoryCategoryBrowserCounts(groupId: AccessoryCategoryBrowserGroupId) {
  const products = getAccessoryCategoryCatalogProducts(groupId);

  return {
    all: products.length,
    Bobrick: products.filter((product) => product.brand === "Bobrick").length,
    ASI: products.filter((product) => product.brand === "ASI").length,
    Bradley: products.filter((product) => product.brand === "Bradley").length,
  } as const;
}
